import { Suspense, useEffect, useRef, useState } from 'react';
import type { ComponentType } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import { useStateContext } from '../../contexts/ContextProvider';
import lazyWithRetry from '../../helpers/lazyWithRetry';
import { useAbonnementPush } from '../api/useAbonnementPush';
import type { EtatPermission } from '../api/useAbonnementPush';
import { useNombreDeCatalogues } from '../api/useCatalogues';
import { useDeconnexion } from '../api/useDeconnexion';
import { t } from '../i18n/langue';
import Button from '../components/Button';
import Text from '../components/Text';
import BandeauUrgent from '../components/BandeauUrgent';
import BandeauReseau from '../components/BandeauReseau';
import AmorceNotifications, { CLE_AMORCE_VUE } from '../screens/AmorceNotifications';
import type { MotifAmorce } from '../screens/AmorceNotifications';
import MessagesMenage from '../screens/MessagesMenage';
import Langue from '../screens/Langue';
import EtatNotifications from '../screens/EtatNotifications';
import { CLE_MAINTENANCE, CLE_MENAGE, CLE_MESSAGERIE, ECRANS_MOBILES } from '../config/mobileScreens';
import type { EcranMobile } from '../config/mobileScreens';
import { useEcrans } from '../config/useEcrans';
import { useBadgeApp } from '../hooks/useBadgeApp';
import { useNombreBadgeApp } from '../hooks/useNombreBadgeApp';
import Commandes from '../screens/Commandes';
import Dashboard from '../screens/Dashboard';
import FinDeCourse from '../screens/FinDeCourse';
import Controles from '../screens/Controles';
import Maintenance from '../screens/Maintenance';
import MesChambres from '../screens/MesChambres';
import Messagerie from '../screens/Messagerie';

import { CoqueProvider } from './CoqueContext';
import GardeEcran from './GardeEcran';
import PanneauNavigation from './PanneauNavigation';

/**
 * L'application, une fois la session ouverte — décision #17.
 *
 * Les routes ne sont pas écrites à la main : elles sont **dérivées du registre**,
 * comme le panneau et l'atterrissage. C'est tout l'intérêt de la liste unique —
 * « visible », « atteignable en tapant l'URL » et « choisi comme écran d'accueil »
 * deviennent par construction la même réponse. Sur le web ils vivent à trois
 * endroits, et `defaultRouteOrder` qui contient `/loyalty` sans son flag de module
 * prouve ce que ça coûte.
 *
 * Le corps de chaque écran porté est branché ici, par clé. TypeScript ne peut pas
 * garantir la couverture — `Record<string, …>` accepte les trous en silence, et un
 * écran déclaré porté sans corps rendrait `undefined`. C'est
 * `mobileScreens.test.tsx` qui tient cette promesse, d'où l'export.
 */
/**
 * L'écran Support (équipe TriggerFlow, super admins seulement) est chargé à la demande : son code (fil, aide IA,
 * planning) ne pèse pas sur le démarrage de l'app des hôteliers. Pendant le chargement, la même phrase que le socle.
 */
// lazyWithRetry, comme router.tsx : après un déploiement, un ancien chunk introuvable recharge la page une fois.
const EcranSupport = lazyWithRetry(() => import('../screens/support/EcranSupport'));

export function ChargementSocle() {
    return (
        <div className="flex h-full flex-col items-center justify-center">
            <Text variante="petit" ton="attenue">
                {t('commun.chargement')}
            </Text>
        </div>
    );
}

function EcranSupportDiffere() {
    return (
        <Suspense fallback={<ChargementSocle />}>
            <EcranSupport />
        </Suspense>
    );
}

export const CORPS: Record<string, ComponentType> = {
    support: EcranSupportDiffere,
    messagerie: Messagerie,
    dashboard: Dashboard,
    commandes: Commandes,
    housekeeping: MesChambres,
    maintenance: Maintenance,
    controles: Controles,
    'hk-messages': MessagesMenage,
};

/**
 * Toute route **portée** existe, éligible ou non — c'est la garde qui tranche, pas
 * le routeur. Un écran auquel on n'a pas droit doit produire le message explicite
 * de #17 §6, jamais un « page inconnue » qui ferait croire à une URL cassée.
 */
const ECRANS_PORTES = ECRANS_MOBILES.filter((ecran) => ecran.porte);

/**
 * Ce que l'amorce peut promettre à ce compte — D4, élargi par le lot 3 Odomo.
 *
 * La carte annonce ce qui fera sonner le téléphone. Deux écrans portent un push :
 * la messagerie (« quand un client écrit ») et « Mes chambres » (« quand on vous
 * affecte des chambres », `mobile_path: '/housekeeping'`). **La messagerie prime** :
 * c'est la raison d'être de l'app, et un compte qui a les deux reçoit les deux.
 *
 * Ni l'un ni l'autre (un compte réduit à Commandes, par exemple) : `null`, rien à
 * promettre, pas d'abonnement posé pour rien.
 */
export function motifAmorce(eligibles: EcranMobile[], atterrissage: EcranMobile | null = null): MotifAmorce | null {
    if (eligibles.some((ecran) => ecran.cle === CLE_MESSAGERIE)) return 'messagerie';
    // Le technicien atterrit sur Maintenance : ce qui le fait sonner, ce sont les pannes.
    if (atterrissage?.cle === CLE_MAINTENANCE) return 'maintenance';
    if (eligibles.some((ecran) => ecran.cle === CLE_MENAGE)) return 'menage';
    if (eligibles.some((ecran) => ecran.cle === CLE_MAINTENANCE)) return 'maintenance';
    return null;
}

/**
 * L'amorce des notifications ne se propose qu'à un compte à qui elle a quelque
 * chose à promettre — D4, voir `motifAmorce`.
 *
 * `eligibles.length > 0` mesurait le mauvais axe : un compte réduit à Commandes a
 * un écran porté éligible, mais aucun push ne le concerne.
 *
 * Pure et exportée pour se prouver sans monter toute la coque (mocks de contexte,
 * de routeur, du réseau…). Les deux écrans visés se lisent dans `eligibles`, qui
 * porte déjà la permission ET le module de « Mes chambres ».
 */
export function doitAmorcerNotifications({
    amorceFaite,
    supporte,
    permission,
    eligibles,
}: {
    amorceFaite: boolean;
    supporte: boolean;
    permission: EtatPermission | 'indisponible';
    eligibles: EcranMobile[];
}): boolean {
    return !amorceFaite && supporte && permission === 'default' && motifAmorce(eligibles) !== null;
}

export default function ApplicationMobile() {
    const [panneauOuvert, setPanneauOuvert] = useState(false);
    const { atterrissage, enAttente, eligibles } = useEcrans();
    const { currentFacility } = useStateContext();
    const { reabonner, supporte, permission } = useAbonnementPush();
    const nombreDeCatalogues = useNombreDeCatalogues(currentFacility?.id ?? null);

    // L'icône de l'app suit le même compteur que la pastille du panneau — un seul
    // sondage de 30 s pour les deux, partagé par la clé React Query. Pour l'équipe
    // TriggerFlow, s'y ajoute ce qu'il reste à traiter au support.
    useBadgeApp(useNombreBadgeApp());

    /*
     * Réabonnement au démarrage ET à chaque bascule d'établissement — #20, #25.
     *
     * Le geste couvre les deux cas d'un seul effet, parce que c'est le MÊME appel :
     * « cet appareil est vivant ». Le serveur l'abonne à tous les hôtels du
     * compte, quelle que soit la sélection, et remplace atomiquement.
     *
     * Au démarrage, il tue le quatrième état de #20 — permission accordée,
     * abonnement mort — que rien d'autre ne rattrape : l'employé se croit
     * joignable et ne l'est plus, en silence. Ici, l'état se répare seul.
     *
     * Placé ici et non dans `SelecteurEtablissement` : la bascule n'est pas la
     * seule façon dont l'établissement change (reprise de session, lien profond de
     * #77), et le brancher sur le geste plutôt que sur l'état en aurait manqué la
     * moitié.
     *
     * Ne rend rien, ne bloque rien, n'affiche aucune erreur : un réabonnement raté
     * ne doit pas retenir quelqu'un qui vient lire ses messages. Le démarrage
     * suivant réessaie.
     */
    const derniereSelection = useRef<number | null>(null);

    useEffect(() => {
        if (!currentFacility || derniereSelection.current === currentFacility.id) return;

        derniereSelection.current = currentFacility.id;
        void reabonner();
    }, [currentFacility, reabonner]);

    /*
     * L'amorce des notifications — #21, #79.
     *
     * Placée ICI et pas dans la coque, pour une raison qui n'est pas cosmétique :
     * l'abonnement se rattache à l'établissement SÉLECTIONNÉ, lu sur `X-Facility-Id`.
     * Amorcer avant que `currentFacility` soit résolu produirait un abonnement
     * attaché au mauvais hôtel — et un téléphone qui sonne pour les messages d'un
     * établissement qu'on ne regarde pas.
     *
     * Ne s'affiche qu'une fois : `permission === 'default'` retombe à `granted` ou
     * `denied` dès qu'on a répondu, et « Plus tard » pose le drapeau. Harceler
     * quelqu'un à chaque ouverture est le meilleur moyen d'obtenir le « Ne pas
     * autoriser » définitif qu'on cherche justement à éviter.
     *
     * Tous les hooks avant le premier `return` — y compris celui-ci.
     *
     * Et seulement à un compte qui a la MESSAGERIE ou « Mes chambres » éligible
     * (D4, lot 3 Odomo) : la carte promet ce qui fera sonner le téléphone, et un
     * compte réduit à Commandes n'a aucun push à recevoir.
     */
    const [amorceFaite, setAmorceFaite] = useState(() => {
        try {
            return localStorage.getItem(CLE_AMORCE_VUE) === '1';
        } catch {
            // Stockage indisponible : on considère l'amorce faite plutôt que de la
            // reposer en boucle à quelqu'un qui a peut-être déjà répondu.
            return true;
        }
    });

    const doitAmorcer = doitAmorcerNotifications({ amorceFaite, supporte, permission, eligibles });

    /*
     * La sortie du chargement bloqué — audit 2026-09-23.
     *
     * `/user` peut échouer autrement que par le réseau (compte purgé, jeton
     * invalide côté serveur sans 401 propre…) : sans issue, « Chargement… » était
     * un cul-de-sac. Après 10 s, on propose de se déconnecter plutôt que de
     * laisser quelqu'un fixer un écran qui ne bougera jamais.
     */
    const { seDeconnecter, enCours: deconnexionEnCours } = useDeconnexion();
    const [attenteLongue, setAttenteLongue] = useState(false);
    useEffect(() => {
        // Remis à zéro dès la FIN de l'attente, pas au début de la suivante : sinon
        // une nouvelle attente (bascule, `/user` rechargé) montrerait « Se
        // déconnecter » d'emblée, et au mieux le temps d'un rendu (audit 2026-09-23).
        if (!enAttente) {
            setAttenteLongue(false);
            return;
        }
        const minuteur = window.setTimeout(() => setAttenteLongue(true), 10_000);
        return () => window.clearTimeout(minuteur);
    }, [enAttente]);

    /*
     * Ne jamais rendre avant que l'établissement soit connu (#27).
     *
     * L'axe module se lit sur l'établissement sélectionné : rendre avant, c'est
     * afficher un menu faux pendant une seconde, puis le corriger sous le pouce de
     * quelqu'un qui a déjà commencé à viser.
     */
    /*
     * ⚠️ Le bandeau réseau est AU-DESSUS de la garde d'attente, et c'est tout ce
     * qui le rend utile (#86).
     *
     * Placé dans un écran, il n'apparaissait jamais au moment où il sert le plus :
     * hors ligne, `enAttente` ne se résout pas — `/user` ne répond pas — et l'app
     * reste sur « Chargement… » sans jamais monter le moindre écran. Constaté à
     * l'écran, backend arrêté, sur la première version de ce lot.
     *
     * Un chargement qui ne finit pas est un cul-de-sac ; le même chargement avec
     * la phrase qui l'explique est une information.
     */
    if (enAttente) {
        return (
            <div className="tf-shell flex h-full flex-col bg-main-bg-color">
                <BandeauReseau />
                <BandeauUrgent />
                <div className="flex flex-1 flex-col items-center justify-center gap-3">
                    <Text variante="petit" ton="attenue">
                        {t('commun.chargement')}
                    </Text>
                    {/* `/user` peut échouer autrement que par le réseau : sans sortie,
                        « Chargement… » était un cul-de-sac (audit 2026-09-23). */}
                    {attenteLongue && (
                        <Button
                            libelle={deconnexionEnCours ? t('coque.deconnexionEnCours') : t('coque.seDeconnecter')}
                            variante="discret"
                            disabled={deconnexionEnCours}
                            onPress={() => void seDeconnecter()}
                        />
                    )}
                </div>
            </div>
        );
    }

    if (doitAmorcer) {
        return (
            <AmorceNotifications
                motif={motifAmorce(eligibles, atterrissage) ?? 'messagerie'}
                onTermine={() => setAmorceFaite(true)}
            />
        );
    }

    return (
        <CoqueProvider value={{ ouvrirPanneau: () => setPanneauOuvert(true), nombreDeCatalogues }}>
            {/*
             * Bandeau + écran dans UNE colonne (audit 2026-09-23) : au-dessus d'écrans
             * en h-full, le bandeau faisait dépasser la page, et le composeur passait
             * sous la ligne de flottaison, hors ligne, précisément quand il compte.
             */}
            <div className="flex h-full flex-col">
                {/* Une seule fois, pour tous les écrans : chacun n'a pas à y penser. */}
                <BandeauReseau />

                <div className="min-h-0 flex-1">
                    <Routes>
                        <Route
                            path="/"
                            element={
                                atterrissage ? (
                                    <Navigate to={atterrissage.chemin} replace />
                                ) : (
                                    <FinDeCourse />
                                )
                            }
                        />

                        {ECRANS_PORTES.map((ecran) => {
                            const Corps = CORPS[ecran.cle];
                            return (
                                <Route
                                    key={ecran.cle}
                                    path={`${ecran.chemin}/*`}
                                    element={
                                        <GardeEcran>
                                            {/*
                                             * Pas d'en-tête ici : il appartient à l'ÉCRAN, qui
                                             * sait ce qu'il a à dire. En poser un au-dessus de
                                             * tout empilait deux barres sur le fil et sur les
                                             * conversations fermées — deux `h1`, et 120 px de
                                             * chrome sur 844 pour un seul bouton utile.
                                             */}
                                            <div className="tf-shell h-full">
                                                <Corps />
                                            </div>
                                        </GardeEcran>
                                    }
                                />
                            );
                        })}

                        {/*
                          * L'état des notifications — HORS REGISTRE, comme `/diagnostic`.
                          *
                          * Une entrée au registre compterait dans les seuils 0/1/≥2 de #17,
                          * et un compte n'ayant que la messagerie basculerait de « un seul
                          * écran » à « deux » alors que rien n'a changé pour lui. C'est une
                          * sous-vue du compte, pas un écran — d'où une route explicite, et
                          * une entrée dans le bloc compte du panneau plutôt que dans sa
                          * navigation.
                          *
                          * Hors garde également : le droit de voir si son propre téléphone
                          * est joignable n'est conditionné à aucun module.
                          */}
                        <Route path="/notifications" element={<EtatNotifications />} />
                        {/* La langue de l'app : même statut que l'état des notifications. */}
                        <Route path="/langue" element={<Langue />} />

                        {/* Chemin inconnu — non porté, mal tapé, ou notification d'un écran
                            qui n'existe pas encore. La garde produit la même phrase neutre
                            que pour un droit retiré : on ne distingue pas les deux cas, et
                            c'est voulu — la distinction renseignerait sur ce qui existe. */}
                        <Route path="*" element={<GardeEcran />} />
                    </Routes>
                </div>
            </div>

            {/* Le bouton qui l'ouvre n'existe que dans l'en-tête d'un écran. La fin
                de course et le message de la garde n'en ont pas : ils portent
                eux-mêmes leur déconnexion et leur sélecteur, ce qui est justement
                pourquoi ils peuvent s'en passer. */}
            {panneauOuvert && <PanneauNavigation onClose={() => setPanneauOuvert(false)} />}
        </CoqueProvider>
    );
}
